// Tab «Запуск» of an event (stage J1): the checklist from a new edition to the first sale; each item leads to where it is fixed.
// The server checks the records (event.launchCheck); the domain of the site is asked from «Домены» of that site.
import { useCallback, useEffect, useState } from 'react';
import { Button, Icon, navigate, useScope, useToast } from '@confdeck/core-web';
import { command } from './command.ts';
import type { LaunchCheck, LaunchItem } from '../src/launch.ts';
import s from './conferences.module.css';

type Row = Pick<LaunchItem, 'label' | 'ok' | 'hint' | 'href' | 'fix'> & { key: string };

async function domainRow(c: LaunchCheck): Promise<Row> {
  const label = 'Домен сайта работает';
  if (!c.siteId) return { key: 'domain', label, ok: false, hint: 'Сначала нужен сайт', href: '' };
  if (c.siteMain) return { key: 'domain', label, ok: true, hint: `Основной сайт аккаунта${c.siteUrl ? `: ${c.siteUrl}` : ''}`, href: `/r/${c.siteId}` };
  const r = await fetch(`/api/sites/${c.siteId}/domains`, { credentials: 'same-origin' }).then((x) => x.json()).catch(() => null) as { domains?: { host: string; status: string; lastError: string | null }[] } | null;
  const all = r?.domains ?? [];
  const active = all.find((d) => d.status === 'active');
  const hint = active ? `https://${active.host}` : all[0] ? `${all[0].host}: ${all[0].lastError ?? 'ещё подключается'}` : 'Сайт проекта открывается только по своему домену: добавьте его во вкладке «Домены» сайта';
  return { key: 'domain', label, ok: !!active, hint, href: `/r/${c.siteId}` };
}

export function EventLaunch({ recordId }: { recordId: string }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [, setScope] = useScope();
  const toast = useToast();
  const load = useCallback(async () => {
    const r = await command<LaunchCheck>('event.launchCheck', { id: recordId });
    if (!r.ok) { setError(r.message); return; }
    const c = r.result;
    setError(null);
    setRows([...c.items, await domainRow(c)]);
  }, [recordId]);
  useEffect(() => { void load(); }, [load]);
  const fix = async (f: NonNullable<Row['fix']>) => {
    setBusy(true);
    const r = await command(f.command, f.input);
    if (!r.ok) toast({ kind: 'error', text: r.message });
    await load();
    setBusy(false);
  };
  // The sections open in this event's scope: a new offer made there belongs to this event.
  const go = (href: string) => { setScope(recordId); navigate(href); };
  if (error) return <p className={s.tplHint}>{error}</p>;
  if (!rows) return <p className={s.tplHint}>Проверяем…</p>;
  const left = rows.filter((x) => !x.ok).length;
  return (
    <section className={s.launch} aria-label="Запуск">
      <div className={s.launchHead}>
        <h3 className={s.tplHead}>{left ? `До запуска — ${left} из ${rows.length}` : 'Всё готово к продажам'}</h3>
        <Button size="sm" variant="ghost" onClick={() => void load()}>Проверить снова</Button>
      </div>
      <ul className={s.launchList}>
        {rows.map((x) => (
          <li key={x.key} className={s.launchRow} data-ok={x.ok ? 'yes' : 'no'}>
            <span className={x.ok ? s.launchOk : s.launchNo}><Icon name={x.ok ? 'tick' : 'close'} size={14} /></span>
            <span className={s.launchText}><span>{x.label}</span><span className={s.tplHint}>{x.hint}</span></span>
            {x.fix && !x.ok && <Button size="sm" variant="primary" disabled={busy} onClick={() => void fix(x.fix!)}>{x.fix.label}</Button>}
            {x.href && !x.ok && !x.fix && <Button size="sm" onClick={() => go(x.href)}>Исправить</Button>}
            {x.href && (x.ok || x.fix) && <Button size="sm" variant="ghost" onClick={() => go(x.href)}>Открыть</Button>}
          </li>
        ))}
      </ul>
    </section>
  );
}
